import * as THREE from 'three';
import { paintSurface } from './surfaces.js';
import { LIGHTS, ceilingOf, floorOf, lightOf, propSurface, roomIndex, wallOf } from './rooms.js';

/**
 * The building's surfaces, laid over the greybox.
 *
 * Every box `build.js` drew keeps its geometry and its place; what changes is
 * its material and its texture coordinates. Each face of a box is dressed for
 * the room it faces — the ballroom side of the run's wall is damask, the run's
 * side is block — and textures are laid in world metres, so the floor of one
 * room runs into the next without a seam and a tile is a foot wherever it is.
 *
 * The light a room is lit by is baked into its own surfaces as a light map: a
 * pool on the floor under each fixture, a wash down the walls, a glow round
 * each fitting on the ceiling. It is what makes a room read as lit by its own
 * lights whichever room the camera is in, and it costs a texture read rather
 * than a light.
 *
 * Collision is not here and is not reachable from here: the boxes the game
 * collides with are `level.solids`, built before any of this and never handed
 * to it.
 */

const FT = 0.3048;
/** BoxGeometry's faces, in its own order. */
const NORMALS = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];

/** Textures, painted once and shared. */
export function createTextureBank({ anisotropy = 4 } = {}) {
  const painted = new Map();
  const maps = new Map();

  function data(pixels, size, srgb) {
    const texture = new THREE.DataTexture(pixels, size, size, THREE.RGBAFormat);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = anisotropy;
    if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    return texture;
  }

  function surface(name) {
    if (!painted.has(name)) {
      const s = paintSurface(name);
      const color = data(s.color, s.size, true);
      // Laid in metres: one repeat is `metres` of surface.
      color.repeat.set(1 / s.metres, 1 / s.metres);
      /*
       * One roughness a surface, the average of what was painted, rather than
       * a second texture read on every pixel of the building. Measured in
       * `perf.mjs`: the map cost a tenth of the drawn frame and was invisible
       * at the distance anybody looks at a floor from.
       */
      let sum = 0;
      for (let i = 1; i < s.rough.length; i += 4) sum += s.rough[i];
      painted.set(name, { color, roughness: sum / (s.rough.length / 4) / 255, metres: s.metres });
    }
    return painted.get(name);
  }

  /**
   * A light map, per kind of light and per kind of surface, with the light's
   * colour in it. Small: the pools are soft and the texture is stretched over
   * a whole fixture's worth of floor.
   */
  function lightMap(light, role) {
    const key = `${light}|${role}`;
    if (maps.has(key)) return maps.get(key);
    const size = 64;
    const px = new Uint8Array(size * size * 4);
    const colour = new THREE.Color(LIGHTS[light].colour);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const u = (x + 0.5) / size;
        const v = (y + 0.5) / size;
        let k;
        if (role === 'floor') {
          // Brightest under the fitting, never dark between: a fitting lights
          // more than its own square.
          const d = Math.hypot(u - 0.5, v - 0.5);
          k = 0.55 + 0.45 * Math.exp(-(d * d) / 0.06);
        } else if (role === 'ceiling') {
          const d = Math.hypot(u - 0.5, v - 0.5);
          k = 0.45 + 0.4 * Math.exp(-(d * d) / 0.03);
        } else {
          // A wall: v is height as a fraction of the room. A scallop under each
          // fitting, bright low down, falling off toward the ceiling, and dark
          // right in the corners where the light does not get.
          const scallop = 0.75 + 0.25 * Math.exp(-((u - 0.5) ** 2) / 0.05);
          const fall = 1 - 0.55 * v * v;
          const foot = Math.min(1, 0.55 + v * 9);
          const head = Math.min(1, 0.6 + (1 - v) * 6);
          k = scallop * fall * foot * head;
        }
        const i = (y * size + x) * 4;
        px[i] = Math.min(255, colour.r * k * 255);
        px[i + 1] = Math.min(255, colour.g * k * 255);
        px[i + 2] = Math.min(255, colour.b * k * 255);
        px[i + 3] = 255;
      }
    }
    const texture = data(px, size, false);
    texture.wrapT = role === 'wall' ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
    texture.channel = 1;
    maps.set(key, texture);
    return texture;
  }

  return {
    surface,
    lightMap,
    dispose() {
      for (const s of painted.values()) s.color.dispose();
      for (const t of maps.values()) t.dispose();
    },
  };
}

/**
 * Where a room's fixtures are, in metres: a grid at the light's spacing,
 * centred in the room. The floor pools and the fittings drawn on the ceiling
 * are both placed from this, so they agree.
 */
export function fixtureGrid(room, spacingFeet) {
  const [x0, z0, x1, z1] = room.rect;
  const minX = Math.min(x0, x1);
  const minZ = Math.min(z0, z1);
  const lx = Math.abs(x1 - x0);
  const lz = Math.abs(z1 - z0);
  const nx = Math.max(1, Math.round(lx / spacingFeet));
  const nz = Math.max(1, Math.round(lz / spacingFeet));
  const sx = (lx / nx) * FT;
  const sz = (lz / nz) * FT;
  const points = [];
  for (let i = 0; i < nx; i += 1) for (let k = 0; k < nz; k += 1) points.push({ x: minX * FT + (i + 0.5) * sx, z: minZ * FT + (k + 0.5) * sz });
  return { minX: minX * FT, minZ: minZ * FT, sx, sz, points };
}

/**
 * Dresses every box of the level in place.
 *
 * `plan` is a plain copy of the level data: rooms, walls and props, in feet.
 * Returns what it made, for disposal, and the names of meshes it hid because
 * something in `dressing.js` draws them instead.
 */
export function dressSurfaces(levelObject, plan, bank, { environment = null } = {}) {
  const roomAt = roomIndex(plan.rooms);
  const roomsById = new Map(plan.rooms.map((room) => [room.id, room]));
  const grids = new Map();
  const gridOf = (room) => {
    if (!grids.has(room.id)) grids.set(room.id, fixtureGrid(room, LIGHTS[lightOf(room)]?.spacing ?? 10));
    return grids.get(room.id);
  };
  const materials = new Map();

  function material({ surface, tint = 0xffffff, light = null, role = null, flat = null }) {
    const key = `${surface}|${tint}|${light}|${role}|${flat}`;
    if (materials.has(key)) return materials.get(key);
    let made;
    if (!surface) {
      made = new THREE.MeshStandardMaterial({ color: flat, roughness: 0.9 });
    } else {
      const s = bank.surface(surface);
      made = new THREE.MeshStandardMaterial({
        color: tint,
        map: s.color,
        roughness: s.roughness,
        metalness: surface === 'steel' ? 0.6 : 0,
      });
      // Things that shine get something to shine with: steel, and the marble.
      if (environment && (surface === 'steel' || surface === 'marble' || surface === 'parquet' || surface === 'tile')) {
        made.envMap = environment;
        made.envMapIntensity = surface === 'steel' ? 0.9 : 0.35;
      }
      if (light && LIGHTS[light]) {
        made.lightMap = bank.lightMap(light, role);
        made.lightMapIntensity = LIGHTS[light].fill;
      }
    }
    materials.set(key, made);
    return made;
  }

  for (const mesh of levelObject.children) {
    if (!mesh.isMesh || !mesh.geometry?.attributes?.normal) continue;
    const name = mesh.name;
    const geometry = mesh.geometry;
    const params = geometry.parameters ?? {};
    const half = { x: (params.width ?? 0) / 2, y: (params.height ?? 0) / 2, z: (params.depth ?? 0) / 2 };
    const faceMaterials = [];

    for (let f = 0; f < 6; f += 1) {
      const n = NORMALS[f];
      const centre = {
        x: mesh.position.x + n[0] * half.x,
        y: mesh.position.y + n[1] * half.y,
        z: mesh.position.z + n[2] * half.z,
      };
      let spec = null;
      let room = null;
      let role = null;

      if (name.startsWith('floor:')) {
        room = roomsById.get(name.slice(6));
        spec = { surface: floorOf(room) };
        role = n[1] > 0 ? 'floor' : null;
      } else if (name.startsWith('ceiling:')) {
        room = roomsById.get(name.slice(8));
        if (n[1] < 0) {
          spec = { surface: ceilingOf(room) };
          role = 'ceiling';
        } else {
          // The roof, from outside: a light membrane.
          spec = { surface: 'concrete', tint: 0xd8d4cc };
          room = null;
        }
      } else if (name.startsWith('prop:')) {
        const found = propSurface(name.slice(5));
        if (found) spec = { surface: found.surface, tint: found.keepColour ? lighten(mesh.material.color) : found.tint ?? 0xffffff };
        room = roomAt(centre.x, centre.z);
      } else if (name.startsWith('barrier:') || name.startsWith('swipe:')) {
        spec = null;
      } else {
        // A wall piece: dressed for whichever room each face looks into.
        if (n[1] !== 0) {
          spec = { surface: 'concrete', tint: 0xc8c4bc };
        } else {
          // Sampled along the face, not just at its middle: a long wall can
          // face a room for part of its length and nothing for the rest, and
          // it is the room that anybody sees it from.
          room = faceRoom(roomAt, centre, n, half);
          spec = wallOf(room);
          role = 'wall';
        }
      }

      if (!spec) {
        faceMaterials.push(mesh.material);
        continue;
      }
      const light = room && role ? lightOf(room) : null;
      faceMaterials.push(material({ ...spec, light: light === 'outdoors' ? null : light, role: light === 'outdoors' ? null : role }));
      layFace(geometry, f, mesh.position, room && role && light !== 'outdoors' ? { grid: gridOf(room), role, ceiling: (room.ceiling ?? 9) * FT, base: (room.base ?? 0) * FT, normal: n } : null);
    }
    if (faceMaterials.some((m) => m !== mesh.material)) {
      mesh.material = faceMaterials;
      geometry.attributes.uv.needsUpdate = true;
      if (geometry.attributes.uv1) geometry.attributes.uv1.needsUpdate = true;
    }
  }

  const byLight = new Map();
  for (const [key, made] of materials) {
    const light = key.split('|')[2];
    if (!byLight.has(light)) byLight.set(light, []);
    byLight.get(light).push(made);
  }
  return {
    materials: [...materials.values()],
    /** Every material lit by a kind of light, so its fill can be turned up or down. */
    byLight,
    grids,
    dispose() {
      for (const m of materials.values()) m.dispose();
    },
  };
}

/** A prop's flat colour, lifted so a texture multiplied by it is not black. */
function lighten(colour) {
  const c = colour.clone();
  const max = Math.max(c.r, c.g, c.b, 0.001);
  c.multiplyScalar(Math.min(2.2, 0.85 / max));
  return c.getHex();
}

/**
 * Writes one face's texture coordinates: `uv` in world metres (the texture's
 * repeat turns metres into tiles), and `uv1` into the room's fixture grid for
 * the light map.
 */
function layFace(geometry, face, origin, lit) {
  const position = geometry.attributes.position;
  const uv = geometry.attributes.uv;
  if (!geometry.attributes.uv1) geometry.setAttribute('uv1', new THREE.BufferAttribute(new Float32Array(position.count * 2).fill(0.5), 2));
  const uv1 = geometry.attributes.uv1;
  // BoxGeometry writes four vertices a face, in face order.
  const n = NORMALS[face];
  for (let i = face * 4; i < face * 4 + 4; i += 1) {
    const x = position.getX(i) + origin.x;
    const y = position.getY(i) + origin.y;
    const z = position.getZ(i) + origin.z;
    if (n[0] !== 0) uv.setXY(i, z * -n[0], y);
    else if (n[1] !== 0) uv.setXY(i, x, z * -n[1]);
    else uv.setXY(i, x * n[2], y);
    if (!lit) continue;
    const { grid, role, ceiling, base } = lit;
    if (role === 'floor' || role === 'ceiling') uv1.setXY(i, (x - grid.minX) / grid.sx, (z - grid.minZ) / grid.sz);
    else {
      const along = n[0] !== 0 ? (z - grid.minZ) / grid.sz : (x - grid.minX) / grid.sx;
      uv1.setXY(i, along, (y - base) / ceiling);
    }
  }
}

/** The room a wall face mostly looks into, preferring any room over none. */
function faceRoom(roomAt, centre, n, half) {
  const counts = new Map();
  const span = n[0] !== 0 ? half.z : half.x;
  for (let k = 0; k < 9; k += 1) {
    const t = -span + ((k + 0.5) / 9) * span * 2;
    const x = centre.x + n[0] * 0.3 + (n[0] !== 0 ? 0 : t);
    const z = centre.z + n[2] * 0.3 + (n[0] !== 0 ? t : 0);
    const room = roomAt(x, z);
    if (room) counts.set(room, (counts.get(room) ?? 0) + 1);
  }
  let best = null;
  let most = 0;
  for (const [room, count] of counts) if (count > most) [best, most] = [room, count];
  return best;
}

/**
 * Merges every dressed box of the level into one mesh per material.
 *
 * Dressing each face for the room it looks into turned a box into as many as
 * six draw calls, and the building is four hundred boxes: measured, a
 * thousand draw calls a frame. The level is static, so it is batched: each
 * face's triangles go, in world space, into the mesh for its material, and
 * the original box is hidden.
 *
 * Only boxes this layer dressed, and only visible ones, are batched. A barrier
 * that is switched on and off, the linen rack whose colour says which linen is
 * selected, and anything still in its flat colour stay exactly as `build.js`
 * made them, because something else may change them.
 */
export function batchLevel(levelObject, dressed) {
  const group = new THREE.Group();
  group.name = 'look-level';
  const buckets = new Map();
  levelObject.updateMatrixWorld(true);
  const normalMatrix = new THREE.Matrix3();
  const v = new THREE.Vector3();
  let hidden = 0;

  for (const mesh of levelObject.children) {
    if (!mesh.isMesh || !mesh.visible || !Array.isArray(mesh.material)) continue;
    if (!mesh.material.every((mat) => dressed.has(mat))) continue;
    const g = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
    const pos = g.attributes.position;
    const nor = g.attributes.normal;
    const uv = g.attributes.uv;
    const uv1 = g.attributes.uv1;
    normalMatrix.getNormalMatrix(mesh.matrixWorld);
    // After toNonIndexed the groups still describe index ranges, now vertex ranges.
    for (const group of mesh.geometry.groups) {
      const material = mesh.material[group.materialIndex];
      const key = `${material.uuid}|${mesh.castShadow}`;
      let bucket = buckets.get(key);
      if (!bucket) buckets.set(key, (bucket = { material, cast: mesh.castShadow, position: [], normal: [], uv: [], uv1: [] }));
      for (let i = group.start; i < group.start + group.count; i += 1) {
        v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
        bucket.position.push(v.x, v.y, v.z);
        v.fromBufferAttribute(nor, i).applyMatrix3(normalMatrix).normalize();
        bucket.normal.push(v.x, v.y, v.z);
        bucket.uv.push(uv.getX(i), uv.getY(i));
        bucket.uv1.push(uv1 ? uv1.getX(i) : 0.5, uv1 ? uv1.getY(i) : 0.5);
      }
    }
    if (g !== mesh.geometry) g.dispose();
    mesh.visible = false;
    hidden += 1;
  }

  const made = [];
  for (const bucket of buckets.values()) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(bucket.position, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(bucket.normal, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(bucket.uv, 2));
    geometry.setAttribute('uv1', new THREE.Float32BufferAttribute(bucket.uv1, 2));
    geometry.computeBoundingSphere();
    const mesh = new THREE.Mesh(geometry, bucket.material);
    mesh.castShadow = bucket.cast;
    mesh.receiveShadow = true;
    group.add(mesh);
    made.push(mesh);
  }
  return {
    object3D: group,
    batched: hidden,
    meshes: made.length,
    dispose() {
      for (const mesh of made) mesh.geometry.dispose();
      group.removeFromParent();
    },
  };
}
